import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Sparkles, X } from "lucide-react";
import api from "@/lib/api";

/**
 * WelcomeChecklist — iter-159 Day-1 welcome strip.
 *
 * Renders the three onboarding milestones (verify email, pick plan,
 * post first job) at the TOP of the Employer Dashboard so a new user
 * hits value in minute one.
 *
 * Hides automatically once all three are complete. Users can also
 * dismiss it manually (persisted in localStorage so a reload doesn't
 * resurrect it).
 *
 * Styling: progress ring + checkmark rows. Matches the dashboard's
 * card-soft aesthetic so it feels like a native widget, not a modal.
 */
const DISMISS_KEY = "sim_welcome_checklist_dismissed";

export const WelcomeChecklist = () => {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(() => {
        try { return !!localStorage.getItem(DISMISS_KEY); } catch { return false; }
    });

    useEffect(() => {
        let active = true;
        api.get("/employer/welcome-checklist")
            .then((r) => { if (active) setState(r.data); })
            .catch(() => { /* ignore — fallback to hidden */ })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (loading || !state || dismissed || state.all_done) return null;

    const progressPct = Math.round((state.done_count / state.total) * 100);

    const renderCTA = (item) => {
        if (item.done) return null;
        if (item.cta_action === "resend_verification_email") {
            // We don't have a resend endpoint yet; nudge the user to
            // check their inbox and spam folder with a soft tooltip
            // instead of a hard CTA.
            return (
                <span
                    className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1"
                    data-testid={`welcome-cta-${item.key}`}
                    title="We sent you a verification email when you signed up"
                >
                    Check your inbox
                </span>
            );
        }
        return (
            <Link
                to={item.cta_href}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
                data-testid={`welcome-cta-${item.key}`}
            >
                {item.cta_label} →
            </Link>
        );
    };

    return (
        <div
            className="mb-4 card-soft p-5 border-2 border-emerald-500/30 bg-emerald-500/[0.04]"
            data-testid="welcome-checklist"
            data-done-count={state.done_count}
        >
            <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                <div className="shrink-0 h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                    <Sparkles size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-semibold text-foreground text-base">
                            Welcome to Simonara
                        </span>
                        <span
                            className="rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                            data-testid="welcome-progress-badge"
                        >
                            {state.done_count} / {state.total}
                        </span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                        {progressPct}% through setup. Finish these three and you&apos;re live.
                    </p>
                </div>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss welcome checklist"
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1"
                    data-testid="welcome-dismiss"
                >
                    <X size={14} />
                </button>
            </div>
            <ul className="mt-4 space-y-2.5">
                {state.items.map((item) => (
                    <li
                        key={item.key}
                        className="flex items-center gap-3 text-sm"
                        data-testid={`welcome-item-${item.key}`}
                        data-done={item.done ? "true" : "false"}
                    >
                        {item.done
                            ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                            : <Circle size={16} className="text-muted-foreground/60 shrink-0" />
                        }
                        <span className={item.done ? "line-through text-muted-foreground" : "text-foreground"}>
                            {item.label}
                        </span>
                        <span className="ml-auto">
                            {renderCTA(item)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default WelcomeChecklist;
